import { useState } from 'react';
import { api } from '../../lib/api';
import { useAsync } from '../../lib/useAsync';
import { useToast } from '../../lib/toast';
import { Banner, EmptyState, Field, LoadingPage, Tag } from '../../components/ui';
import { CashForecastChart } from './CashForecastChart';
import { Metric, Segmented, Slider, pctLabel, usd } from './shared';

type Horizon = 'fy' | '12' | '24' | '36';
type Scenario = 'optimistic' | 'base' | 'conservative';

interface FundMonth {
  begin: number;
  receipts: number;
  payments: number;
  transfersIn: number;
  transfersOut: number;
  financing: number;
  end: number;
}

interface ForecastRow {
  key: string;
  label: string;
  operating: FundMonth;
  replacement: FundMonth;
  consolidated: { begin: number; receipts: number; payments: number; financing: number; end: number };
  inflows: Record<string, number>;
  outflows: Record<string, number>;
}

interface ForecastSummary {
  yearEndOperatingCash: number | null;
  yearEndReplacementCash: number | null;
  yearEndConsolidatedCash: number | null;
  operatingNetCashFlow: number;
  lowestOperatingCash: { key: string; label: string; amount: number } | null;
  firstBelowMinimumLabel: string | null;
  firstNegativeLabel: string | null;
  runwayMonths: number | null;
  runwayBeyondHorizon: boolean;
  notDepleting: boolean;
  fiscalYearEndLabel: string | null;
}

interface ScenarioBlock {
  assumptions: Record<string, unknown>;
  overrides: Record<string, number | null>;
  summary: ForecastSummary;
  rows: ForecastRow[];
}

export function Forecast() {
  const { notify, fail } = useToast();
  const [horizon, setHorizon] = useState<Horizon>('fy');
  const [scenario, setScenario] = useState<Scenario>('base');
  const state = useAsync(() => api.finForecast(horizon), [horizon]);
  const data = state.data as Record<string, unknown> | null;

  if (state.error && /Approve a monthly statement/.test(state.error)) {
    return <EmptyState title="Approve a monthly statement first." hint="The forecast starts from the latest approved cash balances, not from accrual net income." />;
  }
  if (!data) return state.loading ? <LoadingPage label="Loading the cash forecast…" /> : null;

  const scenarios = data.scenarios as Record<Scenario, ScenarioBlock>;
  const current = scenarios[scenario];
  const minCash = data.minOperatingCash as number | null;
  const provisional = data.provisional as { isProvisional: boolean; reasons: string[] };
  const history = (data.history as Array<{ key: string; label: string; operatingCash: number }>) ?? [];
  const scheduled = (data.scheduled as Array<{ id: string; kind: string; label: string; fund: string; dueOn: string; amount: number }>) ?? [];
  const impacts = (data.impacts as Array<{ projectId: string; name: string; yearEndOperatingCash: number | null }>) ?? [];
  const explanation = data.explanation as Record<string, string>;
  const asOf = data.asOf as { key: string; label: string };
  const labels = (data.componentLabels as Record<string, string>) ?? {};

  const points = (() => {
    const map = new Map<string, { key: string; label: string; actual: number | null; projected: number | null }>();
    for (const h of history) map.set(h.key, { key: h.key, label: h.label, actual: h.operatingCash, projected: null });
    for (const r of current.rows) {
      const existing = map.get(r.key) ?? { key: r.key, label: r.label, actual: null, projected: null };
      existing.projected = r.operating.end;
      map.set(r.key, existing);
    }
    return [...map.values()];
  })();

  const s = current.summary;
  const a = current.assumptions;
  const below = s.firstBelowMinimumLabel;
  const negative = s.firstNegativeLabel;

  async function saveOverrides(next: Record<string, number | null>) {
    try {
      await api.finForecastAssumptions(scenario, next);
      notify('Assumptions saved. The forecast will refresh.', 'success');
      await state.reload();
    } catch (error) {
      fail(error, 'Could not save those assumptions.');
    }
  }

  return (
    <div>
      <div className="row between" style={{ marginBottom: 8, flexWrap: 'wrap', gap: 8 }}>
        <div>
          <strong>From {asOf.label}</strong>
          <span className="mut"> · cash only, never accrual net income</span>
        </div>
        <Segmented
          label="Horizon"
          value={horizon}
          onChange={setHorizon}
          options={[
            { value: 'fy', label: 'Rest of fiscal year' },
            { value: '12', label: '12 months' },
            { value: '24', label: '24 months' },
            { value: '36', label: '36 months' },
          ]}
        />
      </div>

      {provisional.isProvisional && (
        <Banner kind="warn">
          This projection is provisional. {provisional.reasons.join(' ')} You can set a collection rate below so the board is using a figure it chose.
        </Banner>
      )}

      <div style={{ margin: '10px 0' }}>
        <Segmented
          label="Scenario"
          value={scenario}
          onChange={setScenario}
          options={[
            { value: 'optimistic', label: 'Optimistic' },
            { value: 'base', label: 'Base' },
            { value: 'conservative', label: 'Conservative' },
          ]}
        />
      </div>

      <div className="card">
        <div className="card-head">
          <h3>{scenario === 'base' ? 'Base case' : scenario === 'optimistic' ? 'Optimistic' : 'Conservative'}</h3>
          <Tag tone={negative ? 'bad' : below ? 'warn' : 'ok'}>{negative ? 'Cash goes negative' : below ? 'Below minimum' : 'Above minimum'}</Tag>
        </div>
        <div className="stat-grid">
          <Metric label={`Operating cash, ${s.fiscalYearEndLabel ?? 'year end'}`} value={usd(s.yearEndOperatingCash)} estimate />
          <Metric label="Replacement cash, year end" value={usd(s.yearEndReplacementCash)} estimate />
          <Metric label="Consolidated cash, year end" value={usd(s.yearEndConsolidatedCash)} estimate />
          <Metric label="Operating surplus / (deficit) of cash" value={usd(s.operatingNetCashFlow)} tone={s.operatingNetCashFlow < 0 ? 'bad' : 'ok'} estimate />
          <Metric
            label="Lowest operating cash"
            value={s.lowestOperatingCash ? usd(s.lowestOperatingCash.amount) : '—'}
            hint={s.lowestOperatingCash?.label}
            estimate
          />
          <Metric label="First month below minimum" value={below ?? 'None in this horizon'} tone={below ? 'warn' : 'ok'} estimate />
          <Metric label="First month cash is negative" value={negative ?? 'None in this horizon'} tone={negative ? 'bad' : 'ok'} estimate />
          <Metric
            label="Operating cash runway"
            value={s.notDepleting ? 'Not depleting' : s.runwayMonths === null ? '—' : `${s.runwayMonths.toFixed(1)} mo${s.runwayBeyondHorizon ? '+' : ''}`}
            estimate
          />
        </div>
      </div>

      <div className="card">
        <div className="card-head"><h3>Operating cash</h3><span className="mut">Solid = approved. Dashed = forecast.</span></div>
        <CashForecastChart points={points} threshold={minCash} />
      </div>

      <AssumptionsEditor
        key={`${scenario}-${horizon}-${asOf.key}`}
        assumptions={a}
        overrides={current.overrides}
        onSave={saveOverrides}
        onReset={async () => {
          try {
            await api.finForecastAssumptionsReset(scenario);
            notify('Restored the default assumptions for this scenario.', 'success');
            await state.reload();
          } catch (error) {
            fail(error, 'Could not reset.');
          }
        }}
      />

      <div className="grid two">
        <MonthTable rows={current.rows} />
        <div>
          <ComponentsCard title="Typical monthly inflows" rows={current.rows} bag="inflows" labels={labels} />
          <ComponentsCard title="Typical monthly outflows" rows={current.rows} bag="outflows" labels={labels} />
        </div>
      </div>

      <ScheduledCard scheduled={scheduled} onChanged={() => void state.reload()} />

      {impacts.length > 0 && (
        <div className="card tw">
          <h3>How open projects move the base forecast</h3>
          <table>
            <thead><tr><th>Project</th><th className="num">Change in year-end operating cash</th></tr></thead>
            <tbody>
              {impacts.map((p) => (
                <tr key={p.projectId}><td>{p.name}</td><td className="num">{usd(p.yearEndOperatingCash)}</td></tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="card">
        <h3>How this is calculated</h3>
        {Object.values(explanation).map((text) => <p className="mut" key={text} style={{ margin: '4px 0' }}>{text}</p>)}
      </div>
    </div>
  );
}

function AssumptionsEditor({
  assumptions,
  overrides,
  onSave,
  onReset,
}: {
  assumptions: Record<string, unknown>;
  overrides: Record<string, number | null>;
  onSave: (next: Record<string, number | null>) => Promise<void>;
  onReset: () => Promise<void>;
}) {
  const num = (k: string) => Number(assumptions[k] ?? 0);
  const [draft, setDraft] = useState(() => ({
    collectionRate: num('collectionRate'),
    collectionLagMonths: num('collectionLagMonths'),
    overdueRecoveryRate: num('overdueRecoveryRate'),
    overdueRecoveryMonths: num('overdueRecoveryMonths'),
    expenseInflation: num('expenseInflation'),
    legalMaintenanceMultiplier: num('legalMaintenanceMultiplier'),
    discretionaryMultiplier: num('discretionaryMultiplier'),
    unexpectedPct: num('unexpectedPct'),
    reserveTransferMonthly: num('reserveTransferMonthly'),
    monthlyDues: num('monthlyDues'),
    assessableUnits: num('assessableUnits'),
  }));
  const set = (k: keyof typeof draft) => (v: number) => setDraft((d) => ({ ...d, [k]: v }));

  return (
    <div className="card">
      <div className="card-head">
        <h3>Assumptions</h3>
        {Object.keys(overrides).length > 0 && <Tag tone="info">Edited</Tag>}
      </div>
      <p className="mut">These are visible and editable. Saving applies only to the scenario you are looking at.</p>
      <div className="grid two">
        <Slider label="Collection rate" min={0} max={1} step={0.01} value={draft.collectionRate} display={pctLabel(draft.collectionRate, 0)} onChange={set('collectionRate')} />
        <Slider label="Collection lag (months)" min={0} max={6} step={1} value={draft.collectionLagMonths} display={String(draft.collectionLagMonths)} onChange={set('collectionLagMonths')} />
        <Slider label="Share of overdue balances expected to be recovered" min={0} max={1} step={0.05} value={draft.overdueRecoveryRate} display={pctLabel(draft.overdueRecoveryRate, 0)} onChange={set('overdueRecoveryRate')} />
        <Slider label="Months to recover that share" min={1} max={36} step={1} value={draft.overdueRecoveryMonths} display={String(draft.overdueRecoveryMonths)} onChange={set('overdueRecoveryMonths')} />
        <Slider label="Expense inflation" min={0} max={0.15} step={0.005} value={draft.expenseInflation} display={pctLabel(draft.expenseInflation, 1)} onChange={set('expenseInflation')} />
        <Slider label="Legal and maintenance multiplier" min={0.8} max={1.5} step={0.05} value={draft.legalMaintenanceMultiplier} display={`${draft.legalMaintenanceMultiplier.toFixed(2)}×`} onChange={set('legalMaintenanceMultiplier')} />
        <Slider label="Discretionary spending (maintenance and other)" min={0.5} max={1.2} step={0.05} value={draft.discretionaryMultiplier} display={pctLabel(draft.discretionaryMultiplier, 0)} onChange={set('discretionaryMultiplier')} />
        <Slider label="Unexpected-cost contingency" min={0} max={0.1} step={0.01} value={draft.unexpectedPct} display={pctLabel(draft.unexpectedPct, 0)} onChange={set('unexpectedPct')} />
        <Field label="Monthly dues ($)"><input type="number" value={draft.monthlyDues} onChange={(e) => set('monthlyDues')(Number(e.target.value))} /></Field>
        <Field label="Assessable homes"><input type="number" value={draft.assessableUnits} onChange={(e) => set('assessableUnits')(Number(e.target.value))} /></Field>
        <Field label="Reserve contribution / transfer per month ($)"><input type="number" value={draft.reserveTransferMonthly} onChange={(e) => set('reserveTransferMonthly')(Number(e.target.value))} /></Field>
      </div>
      <div className="btn-row">
        <button type="button" className="btn" onClick={() => void onSave(draft)}>Save assumptions</button>
        <button type="button" className="btn ghost" onClick={() => void onReset()}>Restore scenario defaults</button>
      </div>
    </div>
  );
}

function MonthTable({ rows }: { rows: ForecastRow[] }) {
  return (
    <div className="card tw">
      <h3>Monthly cash movement</h3>
      <table>
        <thead>
          <tr>
            <th>Month</th>
            <th className="num">Op. begin</th>
            <th className="num">Receipts</th>
            <th className="num">Payments</th>
            <th className="num">Op. end</th>
            <th className="num">Reserve end</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.key}>
              <td>{r.label}</td>
              <td className="num">{usd(r.operating.begin)}</td>
              <td className="num">{usd(r.operating.receipts)}</td>
              <td className="num">{usd(r.operating.payments)}</td>
              <td className={`num ${r.operating.end < 0 ? 'bad' : ''}`}>{usd(r.operating.end)}</td>
              <td className="num">{usd(r.replacement.end)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function ComponentsCard({
  title,
  rows,
  bag,
  labels,
}: {
  title: string;
  rows: ForecastRow[];
  bag: 'inflows' | 'outflows';
  labels: Record<string, string>;
}) {
  const first = rows[0];
  if (!first) return null;
  const entries = Object.entries(first[bag]).filter(([, v]) => v !== 0);
  return (
    <div className="card">
      <h3>{title}</h3>
      <p className="mut">First forecast month. Later months follow the same pattern, then inflation and schedules.</p>
      <table>
        <tbody>
          {entries.map(([k, v]) => (
            <tr key={k}><td>{labels[k] ?? k}</td><td className="num">{usd(v)}</td></tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function ScheduledCard({
  scheduled,
  onChanged,
}: {
  scheduled: Array<{ id: string; kind: string; label: string; fund: string; dueOn: string; amount: number }>;
  onChanged: () => void;
}) {
  const { notify, fail } = useToast();
  const [kind, setKind] = useState('payment');
  const [label, setLabel] = useState('');
  const [fund, setFund] = useState('operating');
  const [dueOn, setDueOn] = useState('');
  const [amount, setAmount] = useState('');

  return (
    <div className="card tw">
      <h3>Other scheduled cash</h3>
      <p className="mut">Confirmed receipts, extra payments, fund transfers and external financing. Transfers move cash between funds and do not change consolidated cash.</p>
      {scheduled.length > 0 && (
        <table>
          <tbody>
            {scheduled.map((s) => (
              <tr key={s.id}>
                <td className="nowrap">{s.dueOn}</td>
                <td>{s.label}<div className="mut">{s.kind} · {s.fund}</div></td>
                <td className="num">{usd(s.amount)}</td>
                <td className="num">
                  <button type="button" className="btn tiny ghost" onClick={async () => {
                    try { await api.finScheduledDelete(s.id); notify('Removed.', 'success'); onChanged(); }
                    catch (error) { fail(error, 'Could not remove that item.'); }
                  }}>Remove</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      <div className="row" style={{ gap: 8, flexWrap: 'wrap', alignItems: 'flex-end', marginTop: 8 }}>
        <Field label="Kind">
          <select value={kind} onChange={(e) => setKind(e.target.value)}>
            <option value="payment">Payment</option>
            <option value="receipt">Receipt</option>
            <option value="transfer">Transfer out of this fund</option>
            <option value="financing">External financing</option>
          </select>
        </Field>
        <Field label="Label"><input value={label} onChange={(e) => setLabel(e.target.value)} maxLength={120} /></Field>
        <Field label="Fund">
          <select value={fund} onChange={(e) => setFund(e.target.value)}>
            <option value="operating">Operating</option>
            <option value="replacement">Replacement</option>
          </select>
        </Field>
        <Field label="Date"><input type="date" value={dueOn} onChange={(e) => setDueOn(e.target.value)} /></Field>
        <Field label="Amount ($)"><input value={amount} inputMode="decimal" onChange={(e) => setAmount(e.target.value)} /></Field>
        <button
          type="button"
          className="btn"
          disabled={!label.trim() || !dueOn || Number(amount) <= 0}
          onClick={async () => {
            try {
              await api.finScheduledAdd({ kind, label: label.trim(), fund, dueOn, amount: Number(amount) });
              notify('Scheduled.', 'success');
              setLabel(''); setAmount('');
              onChanged();
            } catch (error) {
              fail(error, 'Could not add that item.');
            }
          }}
        >
          Add
        </button>
      </div>
    </div>
  );
}
